import { Receipt } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useDueBills } from "@/hooks/use-bills";
import { useWallets } from "@/hooks/use-finance";
import { BillError, payBill, undoPayment } from "@/lib/bills";
import type { Bill } from "@/lib/db";
import { formatUGX } from "@/lib/money";
import { formatDay } from "@/lib/time";

function BillRow({ bill, day, wallet }: { bill: Bill; day: string; wallet?: string }) {
	const [paying, setPaying] = useState(false);
	const overdue = bill.next_due < day;

	async function pay() {
		setPaying(true);
		try {
			const payment = await payBill(bill.id);
			toast.success(`Paid: ${bill.name}`, {
				description: `${formatUGX(bill.amount)} · next due ${formatDay(payment.bill.next_due)}`,
				action: {
					label: "Undo",
					onClick: () =>
						void undoPayment(payment).catch(() => toast.error("Could not undo. Try again.")),
				},
			});
		} catch (err) {
			toast.error(
				err instanceof BillError ? err.message : "Could not save on this device. Try again.",
			);
		} finally {
			setPaying(false);
		}
	}

	return (
		<li className="flex min-h-14 items-center gap-3 px-4 py-2">
			<div className="min-w-0 flex-1">
				<p className="truncate font-medium">{bill.name}</p>
				<p className="text-muted-foreground truncate text-xs">
					<span className={overdue ? "text-money-out font-medium" : undefined}>
						{overdue ? `Overdue since ${formatDay(bill.next_due)}` : "Due today"}
					</span>
					{wallet && ` · ${wallet}`}
				</p>
			</div>
			<span className="text-money-out shrink-0 text-sm font-semibold tabular-nums">
				{formatUGX(bill.amount)}
			</span>
			<Button
				size="sm"
				className="h-11 shrink-0 md:h-9"
				aria-label={`Pay: ${bill.name}`}
				disabled={paying}
				onClick={pay}
			>
				Pay
			</Button>
		</li>
	);
}

/** Bills due today or overdue, each with Pay (+ Undo). Hidden when none are due. */
export function BillsDue({ day }: { day: string }) {
	const bills = useDueBills(day);
	const wallets = useWallets();
	if (!bills?.length) return null;
	const walletName = (id: string) => wallets?.find((w) => w.id === id)?.name;

	return (
		<section aria-labelledby="bills-due-heading" className="mt-4">
			<h2 id="bills-due-heading" className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
				<Receipt className="size-4" /> Bills due
			</h2>
			<ul className="bg-card divide-y rounded-xl border" aria-label="Bills due">
				{bills.map((b) => (
					<BillRow key={b.id} bill={b} day={day} wallet={walletName(b.wallet_id)} />
				))}
			</ul>
		</section>
	);
}
